import type { Evaluation, ReasonLanguage } from "./types";

const DATABASE_NAME = "wordphysics";
const DATABASE_VERSION = 1;
const EVALUATIONS_STORE = "evaluations";
const DRAFTS_STORE = "drafts";

export interface CachedEvaluation {
  id: string;
  clueSetId: string;
  word: string;
  candidateValid: boolean;
  evaluation: Evaluation | null;
  savedAt: number;
}

export interface LessonDraft {
  lessonId: string;
  text: string;
  evaluation: Evaluation | null;
  savedAt: number;
}

function evaluationID(clueSetId: string, word: string, language: ReasonLanguage): string {
  return `${clueSetId}\u0000${word}\u0000${language}`;
}

// Entries saved before the language setting existed were always Japanese.
function legacyEvaluationID(clueSetId: string, word: string): string {
  return `${clueSetId}\u0000${word}`;
}

let databasePromise: Promise<IDBDatabase> | null = null;

function openDatabase(): Promise<IDBDatabase> {
  if (databasePromise) return databasePromise;
  databasePromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(EVALUATIONS_STORE)) {
        database.createObjectStore(EVALUATIONS_STORE, { keyPath: "id" });
      }
      if (!database.objectStoreNames.contains(DRAFTS_STORE)) {
        database.createObjectStore(DRAFTS_STORE, { keyPath: "lessonId" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB could not be opened"));
  });
  return databasePromise;
}

async function read<T>(storeName: string, key: IDBValidKey): Promise<T | null> {
  try {
    const database = await openDatabase();
    return await new Promise((resolve, reject) => {
      const request = database.transaction(storeName, "readonly").objectStore(storeName).get(key);
      request.onsuccess = () => resolve((request.result as T | undefined) ?? null);
      request.onerror = () => reject(request.error);
    });
  } catch {
    return null;
  }
}

async function write(storeName: string, value: unknown): Promise<void> {
  try {
    const database = await openDatabase();
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(storeName, "readwrite");
      transaction.objectStore(storeName).put(value);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  } catch {
    // Storage can be unavailable in private browsing. Scoring should still work.
  }
}

export async function getCachedEvaluation(clueSetId: string, word: string, language: ReasonLanguage): Promise<CachedEvaluation | null> {
  const cached = await read<CachedEvaluation>(EVALUATIONS_STORE, evaluationID(clueSetId, word, language));
  if (cached || language !== "ja") return cached;
  return read<CachedEvaluation>(EVALUATIONS_STORE, legacyEvaluationID(clueSetId, word));
}

export function saveCachedEvaluation(clueSetId: string, word: string, language: ReasonLanguage, candidateValid: boolean, evaluation: Evaluation | null): Promise<void> {
  return write(EVALUATIONS_STORE, {
    id: evaluationID(clueSetId, word, language),
    clueSetId,
    word,
    candidateValid,
    evaluation,
    savedAt: Date.now(),
  } satisfies CachedEvaluation);
}

export function getLessonDraft(lessonId: string): Promise<LessonDraft | null> {
  return read<LessonDraft>(DRAFTS_STORE, lessonId);
}

export function saveLessonDraft(lessonId: string, text: string, evaluation: Evaluation | null): Promise<void> {
  return write(DRAFTS_STORE, { lessonId, text, evaluation, savedAt: Date.now() } satisfies LessonDraft);
}

export async function deleteLessonDraft(lessonId: string): Promise<void> {
  try {
    const database = await openDatabase();
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(DRAFTS_STORE, "readwrite");
      transaction.objectStore(DRAFTS_STORE).delete(lessonId);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  } catch {
    // Storage can be unavailable in private browsing.
  }
}
